import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Copy, RefreshCw, Send, Wand2 } from "lucide-react";
import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

const platforms = ['Instagram', 'Twitter', 'LinkedIn', 'Facebook'];
const tones = ['Professional', 'Casual', 'Friendly', 'Inspirational', 'Humorous'];
const regions = [
  { value: 'kenya', label: '🇰🇪 Kenya', languages: ['English', 'Swahili'] },
  { value: 'nigeria', label: '🇳🇬 Nigeria', languages: ['English', 'Pidgin'] },
  { value: 'south-africa', label: '🇿🇦 South Africa', languages: ['English', 'Zulu', 'Afrikaans'] },
  { value: 'ghana', label: '🇬🇭 Ghana', languages: ['English', 'Twi'] },
  { value: 'tanzania', label: '🇹🇿 Tanzania', languages: ['Swahili', 'English'] },
  { value: 'uganda', label: '🇺🇬 Uganda', languages: ['English', 'Luganda'] },
  { value: 'rwanda', label: '🇷🇼 Rwanda', languages: ['Kinyarwanda', 'English', 'French'] },
  { value: 'senegal', label: '🇸🇳 Senegal', languages: ['French', 'Wolof'] },
];

interface GenerationEntry {
  id: string
  platform: string
  language: string
  prompt: string
  result: string
  created_at: string
}

interface AIAssistantProps {
  onSendToPost?: (content: string) => void;
}

export const AIAssistant = ({ onSendToPost }: AIAssistantProps) => {
  const aiAssistantEnabled = useFeatureFlag("ai_assistant_enabled");
  const { toast } = useToast();

  const [prompt, setPrompt] = useState('');
  const [platform, setPlatform] = useState('Instagram');
  const [tone, setTone] = useState('Professional');
  const [region, setRegion] = useState('kenya');
  const [language, setLanguage] = useState('English');
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [generationHistory, setGenerationHistory] = useState<GenerationEntry[]>([]);
  const [optimizing, setOptimizing] = useState(false);
  const [aiEngine, setAiEngine] = useState<'gemini' | 'claude'>('gemini');
  const [contentType, setContentType] = useState('post');
  const [claudeLanguage, setClaudeLanguage] = useState('en');

  if (!aiAssistantEnabled) {
    return (
      <Card className="p-12 text-center">
        <Sparkles className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <h3 className="font-semibold mb-1">AI Content Assistant</h3>
        <p className="text-justify text-sm text-muted-foreground">
          AI content generation has not been enabled for your organisation. Contact your CONORBIT administrator.
        </p>
      </Card>
    );
  }

  const selectedRegion = regions.find(r => r.value === region);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Enter a prompt",
        description: "Please describe what you want to post about",
        variant: "destructive"
      });
      return;
    }

    setGenerating(true);
    setGeneratedContent('');

    try {
      if (aiEngine === 'claude') {
        const { data, error } = await supabase.functions.invoke('generate-claude-content', {
          body: {
            prompt:       prompt.trim(),
            platform:     platform.toLowerCase(),
            language:     claudeLanguage,
            tone:         tone.toLowerCase(),
            content_type: contentType,
          }
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        const claudeResult = data?.content || '';
        setGeneratedContent(claudeResult);
        setGenerationHistory(prev => [{
          id: crypto.randomUUID(), platform, language: claudeLanguage,
          prompt: prompt.trim(), result: claudeResult,
          created_at: new Date().toISOString(),
        }, ...prev.slice(0, 9)]);
        toast({ title: "Content Generated!", description: "Claude content ready." });
      } else {
        const { data, error } = await supabase.functions.invoke('generate-social-content', {
          body: {
            platform: platform.toLowerCase(),
            tone: tone.toLowerCase(),
            prompt: prompt.trim(),
            region: region,
            language: language.toLowerCase(),
          }
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        const content = data.hashtags?.length > 0
          ? `${data.content}\n\n${data.hashtags.map((h: string) => `#${h}`).join(' ')}`
          : data.content;

        setGeneratedContent(content);
        setGenerationHistory(prev => [{
          id: crypto.randomUUID(), platform, language,
          prompt: prompt.trim(), result: content,
          created_at: new Date().toISOString(),
        }, ...prev.slice(0, 9)]);

        toast({
          title: "Content Generated!",
          description: `${data.characterCount}/${data.characterLimit} characters. Estimated ${data.estimatedEngagement} engagement.`,
        });
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleOptimize = async (content: string) => {
    if (!content.trim()) {
      toast({
        title: "No content",
        description: "Please generate content first",
        variant: "destructive"
      });
      return;
    }

    setOptimizing(true);

    try {
      const { data, error } = await supabase.functions.invoke('optimize-post', {
        body: {
          content: content.trim(),
          platform: platform.toLowerCase(),
          region: region,
          language: language.toLowerCase(),
        }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      setGeneratedContent(data.optimizedContent);
      
      toast({
        title: "Content Optimized!",
        description: `Sentiment: ${data.sentiment}. Score: ${data.engagementScore}. ${data.improvements.length} improvements applied.`,
      });
    } catch (error) {
      console.error('Optimization error:', error);
      toast({
        title: "Optimization Failed",
        description: error instanceof Error ? error.message : "Failed to optimize content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setOptimizing(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Content copied to clipboard"
    });
  };

  const handleSendToPost = (content: string) => {
    if (onSendToPost) {
      onSendToPost(content);
      toast({
        title: "Redirecting to Posts",
        description: "Opening post editor with your content"
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <h3 className="text-lg font-semibold mb-4">Generate New Content</h3>

          {/* AI Engine toggle */}
          <div className="mb-4">
            <div className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
              AI Engine
            </div>
            <div className="flex rounded-xl border border-[#DDD8CE] overflow-hidden">
              <button
                onClick={() => setAiEngine('gemini')}
                className={`flex-1 py-2.5 text-[12.5px] font-medium transition-colors ${
                  aiEngine === 'gemini'
                    ? 'bg-[#0D2240] text-white'
                    : 'bg-white text-muted-foreground hover:bg-[#F7F5F0]'
                }`}
              >
                ⚡ Quick · Gemini
              </button>
              <button
                onClick={() => setAiEngine('claude')}
                className={`flex-1 py-2.5 text-[12.5px] font-medium transition-colors ${
                  aiEngine === 'claude'
                    ? 'bg-[#C8A84B] text-[#0D2240]'
                    : 'bg-white text-muted-foreground hover:bg-[#F7F5F0]'
                }`}
              >
                ✦ Premium · Claude
              </button>
            </div>
            {aiEngine === 'claude' && (
              <p className="text-[11px] text-muted-foreground mt-1.5 text-justify">
                Claude generates institutional-quality content with deeper language accuracy
                across all 6 languages.
              </p>
            )}
          </div>

          {/* Claude-specific options */}
          {aiEngine === 'claude' && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Content Type
                </label>
                <select
                  value={contentType}
                  onChange={e => setContentType(e.target.value)}
                  className="w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[12.5px] bg-white focus:outline-none focus:border-[#C8A84B]"
                >
                  <option value="post">Social post</option>
                  <option value="caption">Photo caption</option>
                  <option value="thread">Thread / carousel</option>
                  <option value="press_release">Press release</option>
                  <option value="announcement">Official announcement</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Language
                </label>
                <select
                  value={claudeLanguage}
                  onChange={e => setClaudeLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[12.5px] bg-white focus:outline-none focus:border-[#C8A84B]"
                >
                  <option value="en">🇬🇧 English</option>
                  <option value="fr">🇫🇷 French</option>
                  <option value="pt">🇵🇹 Portuguese</option>
                  <option value="es">🇪🇸 Spanish</option>
                  <option value="ar">🇸🇦 Arabic</option>
                  <option value="sw">🇰🇪 Swahili</option>
                </select>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Platform</label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {platforms.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Tone</label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tones.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Region 🌍</label>
                <Select value={region} onValueChange={(val) => {
                  setRegion(val);
                  const newRegion = regions.find(r => r.value === val);
                  if (newRegion) setLanguage(newRegion.languages[0]);
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Language</label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedRegion?.languages.map(l => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">What would you like to post about?</label>
              <Textarea
                placeholder="E.g., 'Announce our new product feature with focus on user benefits' or 'Share insights about industry trends in East Africa'"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
              />
            </div>

            <Button 
              className="w-full gradient-primary" 
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Content
                </>
              )}
            </Button>

            {generating && (
              <div className="border border-border rounded-lg p-4 bg-muted/30">
                <div className="space-y-2">
                  <div className="h-4 bg-primary/20 rounded animate-pulse" />
                  <div className="h-4 bg-primary/20 rounded animate-pulse w-5/6" />
                  <div className="h-4 bg-primary/20 rounded animate-pulse w-4/6" />
                </div>
              </div>
            )}

            {generatedContent && (
              <div className="border border-border rounded-lg p-4 bg-card">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI Generated
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleOptimize(generatedContent)}
                      disabled={optimizing}
                    >
                      {optimizing ? (
                        <>
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          Optimizing...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-3 h-3 mr-1" />
                          Optimize
                        </>
                      )}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleCopy(generatedContent)}>
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={() => handleSendToPost(generatedContent)}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Use
                    </Button>
                  </div>
                </div>
                <p className="text-justify text-sm whitespace-pre-line">{generatedContent}</p>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">AI Features</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-justify font-medium text-sm">Multilingual Support</p>
                <p className="text-justify text-xs text-muted-foreground">Generate content in multiple languages for regional audiences</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-justify font-medium text-sm">Tone Adaptation</p>
                <p className="text-justify text-xs text-muted-foreground">Match your brand voice across all platforms</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-justify font-medium text-sm">Platform Optimization</p>
                <p className="text-justify text-xs text-muted-foreground">Optimized for each platform's best practices</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-justify font-medium text-sm">Auto To-Do Creation</p>
                <p className="text-justify text-xs text-muted-foreground">Tasks automatically created for review and approval</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Generations</h3>
        {generationHistory.length === 0 ? (
          <p className="text-justify text-[12px] text-muted-foreground text-center py-4">
            Your generated content will appear here during this session.
          </p>
        ) : (
          <div className="space-y-3">
            {generationHistory.map((item) => (
              <div
                key={item.id}
                className="border border-border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setGeneratedContent(item.result)}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Badge variant="secondary" className="text-[10px]">{item.platform}</Badge>
                  <span className="text-[10px] text-muted-foreground uppercase">{item.language}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {new Date(item.created_at).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                    onClick={(e) => { e.stopPropagation(); handleCopy(item.result); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                    onClick={(e) => { e.stopPropagation(); handleSendToPost(item.result); }}>
                    <Send className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  Prompt: "{item.prompt}"
                </p>
                <p className="text-[12px] text-foreground truncate mt-0.5">
                  {item.result.slice(0, 90)}{item.result.length > 90 ? '…' : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
